import React, { useEffect, useState } from 'react';
import { t } from '../../i18n/i18n';
import NewStyleSelect from '../NewStyleSelect/NewStyleSelect';

import { maxRecordTimes, maxPreparationTimes, maxAttemptsOptions } from '../../constants/questions';
import { useQuestionHandlers } from '../../hooks/useQuestionHandlers';
import GenericToggle from '../GenericToggle/GenericToggle';
import DisabledFeature from '../DisabledFeature/DisabledFeature';
import { AnswerTypeEnum, PlanFeaturesIds, PromptSubTypes, MECHANOGRAFY_PPM_TEST } from '../../constants/enums';
import { isAiInterviewStrategy } from '../../utils/testgroups';
import { getAvailablePromptsByStrategy } from '../../utils/prompts';
import DropdownSelect from '../DropdownSelect/DropdownSelect';
import InfoTooltip from '../InfoTooltip/InfoTooltip';
import HorizontalLoader from '../HorizontalLoader/HorizontalLoader';

const QuestionWithVideoAnswer = ({
  readOnly = false,
  testGroupABM = false,
  isTranslateABM = false,
}) => {
  const [promptsCorrectionTextAIOptions, setPromptsCorrectionTextAIOptions] = useState([]);
  const {
    testAnswer,
    setTestAnswer,
    aiImg,
    handleAddQuestionText,
    handleCurrentTypeQuestion,
    handleGenerateAIAnswer,
    loadingAIAnswer,
    currentQuestionData,
    currentTypeQuestion,
    promptsCorrectionTextAI,
    handleAddPrompTextAI,
    customTestGroupStrategy,
  } = useQuestionHandlers(isTranslateABM);

  useEffect(() => {
    const options = promptsCorrectionTextAI
      ?.filter((prompt) =>
        getAvailablePromptsByStrategy(customTestGroupStrategy, prompt.promptSubType)
      )
      ?.map((item) => ({
        id: t(item.promptSubType),
        name: t(item.promptSubType),
        promptId: item.promptSubType,
        promptSubType: item.promptSubType,
      }));
    setPromptsCorrectionTextAIOptions(options);
  }, [promptsCorrectionTextAI, customTestGroupStrategy]);

  return (
    <div data-testid="question-video-answer-container" className="row">
      <QuestionItem dataTestId="question-video-answer-name" className="col-6">
        <textarea
          name="text"
          className={`bottom-border outline-none w-100 p-2 px-3 text-xs text-center  ${currentQuestionData?.text?.length ? 'selected-border' : 'unselected-border'
            }`}
          rows={testGroupABM ? '3' : '1'}
          placeholder={t('CREATE_SCREENING_YOUR_QUESTIONS_WRITE_YOU_QUESTION_PLACEHOLDER')}
          onChange={(e) => handleAddQuestionText(e)}
          value={currentQuestionData?.text ?? ''}
          maxLength={5000}
          data-testid="question-text-textarea"
          disabled={readOnly}
        />
        {(testGroupABM && currentTypeQuestion !== AnswerTypeEnum.VIDEO_AI) && (
          <textarea
            type="text"
            onChange={handleAddQuestionText}
            name="formula"
            value={currentQuestionData?.formula ?? ''}
            className={`w-100 mt-3 py-2 px-3  border-thin-blue-principal outline-none text-xs min-height-add-question ${currentQuestionData?.formula?.length ? 'selected-border' : 'unselected-border'
              }`}
            rows="1"
            maxLength={500}
            placeholder={t('CREATE_SCREENING_YOUR_FORMULAS_PLACEHOLDER')}
            data-testid="question-mc-add-formula-textarea"
            disabled={readOnly}
          />
        )}
        <div className="d-flex flex-column gap-3 mt-4">
          <div
            data-testId="question-video-answer-total-time"
            className="justify-content-start d-flex flex-column w-100"
          >
            <div>
              <span className=" font-italic text-muted font-weight-bold text-xs">
                {t('CREATE_SCREENING_YOUR_QUESTIONS_VIDEO_TOTAL_RECORD_TIME')}
              </span>
            </div>
            <div>
              <NewStyleSelect
                error={false}
                className="w-100"
                value={currentQuestionData?.totalTime ?? ''}
                options={maxRecordTimes}
                onChange={(e) => {
                  handleAddQuestionText(e);
                }}
                name="totalTime"
                borderClasses="bottom-border text-center"
                disabled={readOnly}
              />
            </div>
          </div>
          <div
            data-testId="question-video-answer-preparation-time"
            className="d-flex flex-column w-100 "
          >
            <div>
              <span className=" font-italic text-muted font-weight-bold text-xs">
                {t('CREATE_SCREENING_YOUR_QUESTIONS_VIDEO_PREPARATION_TIME')}
              </span>
            </div>
            <div>
              <NewStyleSelect
                error={false}
                className="w-100"
                value={currentQuestionData?.preparationTime ?? ''}
                options={maxPreparationTimes}
                onChange={(e) => {
                  handleAddQuestionText(e);
                }}
                name="preparationTime"
                borderClasses="bottom-border text-center"
                disabled={readOnly}
              />
            </div>
          </div>
          <div
            data-testId="question-video-answer-max-attempts"
            className="justify-content-start d-flex flex-column w-100"
          >
            <div>
              <span className=" font-italic text-muted font-weight-bold text-xs">
                {t('CREATE_SCREENING_YOUR_QUESTIONS_VIDEO_MAX_ATTEMPTS')}
              </span>
            </div>
            <div>
              <NewStyleSelect
                error={false}
                className="w-100"
                value={currentQuestionData?.maxAttempts ?? ''}
                options={maxAttemptsOptions}
                onChange={(e) => {
                  handleAddQuestionText(e);
                }}
                name="maxAttempts"
                borderClasses="bottom-border text-center"
                disabled={readOnly}
              />
            </div>
          </div>
        </div>
      </QuestionItem>
      <div className="col-6 d-flex flex-column gap-3">
        {testGroupABM ? (
          <div className="d-flex align-items-center gap-1">
            <span className="ai-image-container">
              {aiImg}
              {t('QUESTION_TEXT_TOGGLE_AI_CORRECTION')}
            </span>
            <GenericToggle
              actionFunction={() => {
                if (currentTypeQuestion !== AnswerTypeEnum.VIDEO_AI) {
                  handleCurrentTypeQuestion(AnswerTypeEnum.VIDEO_AI);
                } else {
                  handleCurrentTypeQuestion(AnswerTypeEnum.VIDEO);
                }
              }}
              defaultActive={currentTypeQuestion === AnswerTypeEnum.VIDEO_AI}
              disabled={readOnly}
            />
          </div>
        ) : (
          <DisabledFeature
            className="d-flex flex-row"
            iconClassName="align-self-start"
            childrenClassname="mt-2 pt-1"
            featureId={PlanFeaturesIds.PROCTORING}
          >
            <div className="d-flex align-items-center gap-1 ai-image-container">
              {aiImg}
              <span className=" text-xs font-weight-500">
                {t('QUESTION_TEXT_TOGGLE_AI_CORRECTION')}
              </span>
              {!isAiInterviewStrategy(customTestGroupStrategy) && (
                <GenericToggle
                  className="svgs-text-ai"
                  disabled={testGroupABM || readOnly}
                  actionFunction={() => {
                    if (currentTypeQuestion !== AnswerTypeEnum.VIDEO_AI) {
                      handleCurrentTypeQuestion(AnswerTypeEnum.VIDEO_AI);
                    } else {
                      handleCurrentTypeQuestion(AnswerTypeEnum.VIDEO);
                    }
                  }}
                  defaultActive={currentTypeQuestion === AnswerTypeEnum.VIDEO_AI}
                />
              )}
            </div>
          </DisabledFeature>
        )}
        {currentTypeQuestion === AnswerTypeEnum.VIDEO_AI && (
          <div className="d-flex flex-column align-items-start gap-1 mt-3">
            <div className="d-flex flex-column w-100">
              <span className=" text-xs font-weight-400 min-height-ia-text d-flex align-items-start">
                {t(`QUESTION_TEXT_TOGGLE_AI_ADD_VIDEO`)}
                <InfoTooltip
                  className="ml-2 info-tooltip-ai"
                  placement="top"
                  content={
                    <div className="d-flex flex-column py-2">
                      <span className=" text-xs mb-2">
                        {t(
                          `QUESTION_TEXT_TOGGLE_AI_ADD_VIDEO_FIRST_DISCLAIMER`
                        )}
                      </span>
                    </div>
                  }
                />
              </span>
              <textarea
                name="concepts"
                className={`bottom-border outline-none w-100 mt-3 p-2 px-3 text-xs font-weight-400  ${currentQuestionData?.concepts?.length
                  ? 'selected-border'
                  : 'unselected-border'
                  }`}
                rows="3"
                onChange={(e) => handleAddQuestionText(e)}
                value={currentQuestionData?.concepts ?? ''}
                maxLength={1000}
                data-testid="question-video-ai-concepts-textarea"
                disabled={readOnly || isTranslateABM}
              />
            </div>
            {testGroupABM && currentTypeQuestion === AnswerTypeEnum.VIDEO_AI && (
              <div className="d-flex flex-column w-100 mt-3">
                <span className="text-xs font-weight-400">
                  {t('CREATE_TEST_GROUP_SCORE')}
                </span>
                <input
                  type="number"
                  name="weight"
                  className="bottom-border outline-none w-100 mt-1 p-2 px-3 text-xs font-weight-400"
                  onChange={(e) => handleAddQuestionText(e)}
                  value={currentQuestionData?.weight || 1}
                  min={1}
                  max={10}
                  data-testid="question-video-ai-weight-input"
                  disabled={readOnly}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const QuestionItem = ({ children, dataTestId, className = '' }) => (
  <div data-testid={dataTestId} className={`${className} justify-content-between`}>
    {children}
  </div>
);

export default QuestionWithVideoAnswer;
